import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import { createOnboardingSession } from '../utils/stripeOnboarding';
import { STRIPE_PUBLISHABLE_KEY } from '../context/stripeContext';
import {
    createStripeConnectComponent,
    initializeStripeConnect,
} from '../utils/stripeConnect';
import '../styles/pages/StripeEmbeddedOnboardingPage.css';

const StripeEmbeddedOnboardingPage = () => {
    const containerRef = useRef(null);
    const navigate = useNavigate();
    const { getIdToken, userAttributes } = useAuth();
    const [errorMessage, setErrorMessage] = useState('');
    const [attempt, setAttempt] = useState(0);
    const [status, setStatus] = useState('Loading secure payout setup…');
    const onboardingElementRef = useRef(null);
    const attributesRef = useRef(userAttributes);
    attributesRef.current = userAttributes;

    useEffect(() => {
        let cancelled = false;
        let onboardingElement;
        let pendingSecret;
        const controller = new AbortController();
        const showError = message => {
            if (!cancelled) {
                controller.abort();
                setStatus('');
                setErrorMessage(current => current || message || 'Unable to load payout setup. Please try again.');
            }
        };

        const fetchClientSecret = () => {
            if (cancelled) return Promise.reject(new DOMException('Payout setup interrupted', 'AbortError'));
            // Share concurrent SDK requests, but allow it to renew a session later.
            if (pendingSecret) return pendingSecret;
            pendingSecret = createOnboardingSession({
                getIdToken,
                // ProtectedRoute already waits for loaded account attributes.
                address: attributesRef.current?.address,
                signal: controller.signal,
                onRetry: () => { if (!cancelled) setStatus('Payout setup is taking longer than usual. Retrying…'); },
            }).then(secret => {
                if (!cancelled) setStatus('');
                return secret;
            }).catch(error => {
                // Stripe may call this after mountOnboarding has completed.
                if (error.name !== 'AbortError') showError(error.message);
                throw error;
            }).finally(() => { pendingSecret = null; });
            return pendingSecret;
        };

        const mountOnboarding = async () => {
            try {
                const connectInstance = await initializeStripeConnect({
                    publishableKey: STRIPE_PUBLISHABLE_KEY,
                    fetchClientSecret,
                    appearance: {
                        overlays: 'dialog',
                        variables: {
                            colorPrimary: '#222222',
                            colorBackground: '#ffffff',
                            colorText: '#1f1f1f',
                            borderRadius: '12px',
                        },
                    },
                });

                onboardingElement = await createStripeConnectComponent(
                    connectInstance,
                    'account-onboarding'
                );
                if (cancelled) return;
                onboardingElementRef.current = onboardingElement;

                if (typeof onboardingElement.setOnExit === 'function') {
                    onboardingElement.setOnExit(() => { if (!cancelled) navigate('/'); });
                }
                if (typeof onboardingElement.setOnLoadError === 'function') {
                    onboardingElement.setOnLoadError(() => showError('Unable to load payout setup. Please try again.'));
                }

                if (cancelled || !containerRef.current) {
                    return;
                }

                containerRef.current.replaceChildren(onboardingElement);
            } catch (error) {
                console.error('[StripeEmbeddedOnboardingPage] onboarding error:', error);
                showError('Unable to load payout setup. Please try again.');
            }
        };

        mountOnboarding();

        return () => {
            cancelled = true;
            controller.abort();
            if (onboardingElement?.parentNode) {
                onboardingElement.parentNode.removeChild(onboardingElement);
            }
            if (onboardingElementRef.current === onboardingElement) {
                onboardingElementRef.current = null;
            }
        };
    }, [attempt, getIdToken, navigate]);

    const retryOnboarding = () => {
        setErrorMessage('');
        setStatus('Loading secure payout setup…');
        setAttempt(value => value + 1);
    };

    const handleSkipPayoutSetup = () => {
        const onboardingElement = onboardingElementRef.current;

        if (onboardingElement?.parentNode) {
            onboardingElement.parentNode.removeChild(onboardingElement);
        }

        onboardingElementRef.current = null;
        navigate('/');
    };

    return (
        <main className="stripe-embedded-page">
            <section className="stripe-embedded-shell">
                <header className="stripe-embedded-intro">
                    <span className="stripe-embedded-kicker">Payout setup</span>
                    <h1>Set up payouts with Stripe</h1>
                    <p>
                        Stripe securely collects the financial and identity details needed to send
                        your earnings. You can leave and come back to finish this later.
                    </p>
                </header>

                <div className="stripe-embedded-actions">
                    <button
                        type="button"
                        className="skip-profile-button stripe-embedded-skip"
                        onClick={handleSkipPayoutSetup}
                    >
                        Skip Payout Setup For Now
                    </button>
                </div>

                {errorMessage ? (
                    <div className="stripe-embedded-error" role="alert">
                        <h2>We couldn't load payout setup</h2>
                        <p>{errorMessage}</p>
                        <div className="stripe-embedded-error-actions">
                            <button type="button" onClick={retryOnboarding}>
                                Try again
                            </button>
                            <Button type="button" variant="secondary" onClick={handleSkipPayoutSetup}>
                                Skip Payout Setup For Now
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        {status && <p role="status">{status}</p>}
                        <div className="stripe-embedded-component" ref={containerRef} />
                    </>
                )}
            </section>
        </main>
    );
};

export default StripeEmbeddedOnboardingPage;
